import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
// Import the dashboard components from their respective files
// but rename them to avoid name conflicts with local components
import StudioOwnerDashboardComponent from '@/components/dashboard/StudioOwnerDashboard';
import StaffDashboardComponent from '@/components/dashboard/StaffDashboard';

// Mock bookings for the dashboard
const mockUserBookings = [/* ... keep existing mock data ... */];

const Dashboard = () => {
  const { isAuthenticated, userRole, isLoading } = useAuth();
  const navigate = useNavigate();
  
  // Effect to redirect users to their appropriate dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole) {
      if (userRole === 'studio_owner') {
        navigate('/studio-dashboard');
      } else if (userRole === 'staff') {
        navigate('/staff-dashboard');
      } else if (userRole === 'admin') {
        navigate('/admin');
      }
      // customers stay on this dashboard
    }
  }, [isLoading, isAuthenticated, userRole, navigate]);
  
  // Main dashboard layout based on user role
  const renderDashboardByRole = () => {
    switch (userRole) {
      case 'admin':
        return <AdminDashboard />;
      case 'studio_owner':
        return <StudioDashboardRedirect />;
      case 'staff':
        return <StaffDashboardRedirect />;
      default:
        return <CustomerDashboard />;
    }
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16 flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-chrona-accent" />
          <p className="text-chrona-gray-300">Lade Dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto text-center">
          <h1 className="text-2xl font-bold mb-6">Zugriff auf dein Dashboard</h1>
          
          <Card>
            <CardContent className="pt-6">
              <p className="mb-4">
                Bitte melde dich an, um dein persönliches Dashboard zu sehen.
              </p>
              
              <div className="grid gap-4">
                <Button 
                  className="w-full bg-chrona-accent hover:bg-chrona-accent-light" 
                  onClick={() => navigate('/login')}
                >
                  Anmelden
                </Button>
                
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => navigate('/register')}
                >
                  Registrieren
                </Button>
                
                <p className="text-sm text-gray-600 mt-2">
                  Als Gast fortfahren? <a href="/booking" className="text-chrona-accent hover:underline">Direkt Termin buchen</a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 my-[62px]">
        {userRole === 'admin' && 'Admin-Dashboard'}
        {userRole === 'studio_owner' && 'Studio-Dashboard'}
        {userRole === 'staff' && 'Mitarbeiter-Dashboard'}
        {userRole === 'customer' && 'Mein Dashboard'}
      </h1>
      
      {renderDashboardByRole()}
    </div>
  );
};

// Simple redirect components to avoid import conflicts with the main dashboard components
const StudioDashboardRedirect = () => {
  useEffect(() => {
    window.location.href = '/studio-dashboard';
  }, []);
  return <p>Redirecting to studio dashboard...</p>;
};

const StaffDashboardRedirect = () => {
  useEffect(() => {
    window.location.href = '/staff-dashboard';
  }, []);
  return <p>Redirecting to staff dashboard...</p>;
};

// Customer Dashboard Component - this is a local component
const CustomerDashboard = () => {
  return (
    <Tabs defaultValue="bookings">
      <TabsList className="mb-6">
        <TabsTrigger value="bookings">Meine Buchungen</TabsTrigger>
        <TabsTrigger value="favorites">Favoriten</TabsTrigger>
        <TabsTrigger value="profile">Profil</TabsTrigger>
      </TabsList>
      
      <TabsContent value="bookings">
        <div className="space-y-6">
          {/* Upcoming Bookings */}
          <Card>
            <CardHeader>
              <CardTitle>Kommende Termine</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockUserBookings.filter(booking => booking.status === 'gebucht').map(booking => (
                  <div key={booking.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div>
                        <h3 className="font-medium">{booking.service}</h3>
                        <p className="text-sm text-gray-600">{booking.provider}</p>
                        <div className="flex items-center mt-1 text-sm">
                          <span className="text-chrona-accent">
                            {new Date(booking.date).toLocaleDateString()} um {booking.time} Uhr
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 md:mt-0 flex flex-col md:items-end">
                        <span className="font-semibold text-chrona-text">
                          {booking.price.toFixed(2)} €
                        </span>
                        <div className="mt-2 flex gap-2">
                          <Button variant="outline" className="text-xs h-8" size="sm">
                            Ändern
                          </Button>
                          <Button variant="outline" className="text-destructive hover:bg-destructive hover:text-white text-xs h-8" size="sm">
                            Stornieren
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {mockUserBookings.filter(booking => booking.status === 'gebucht').length === 0 && 
                  <p className="text-gray-600 italic">Keine kommenden Termine.</p>}
              </div>
            </CardContent>
          </Card>
          
          {/* Past Bookings */}
          <Card>
            <CardHeader>
              <CardTitle>Vergangene Termine</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {mockUserBookings.filter(booking => booking.status === 'abgeschlossen').map(booking => (
                  <div key={booking.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row justify-between">
                      <div>
                        <h3 className="font-medium">{booking.service}</h3>
                        <p className="text-sm text-gray-600">{booking.provider}</p>
                        <div className="flex items-center mt-1 text-sm">
                          <span className="text-gray-500">
                            {new Date(booking.date).toLocaleDateString()} um {booking.time} Uhr
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 md:mt-0 flex flex-col md:items-end">
                        <span className="font-semibold text-gray-500">
                          {booking.price.toFixed(2)} €
                        </span>
                        <div className="mt-2">
                          <Button variant="outline" className="text-xs h-8" size="sm">
                            Erneut buchen
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {mockUserBookings.filter(booking => booking.status === 'abgeschlossen').length === 0 && 
                  <p className="text-gray-600 italic">Keine vergangenen Termine.</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
      
      <TabsContent value="favorites">
        <Card>
          <CardHeader>
            <CardTitle>Meine Favoriten</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 italic">Du hast noch keine Anbieter als Favoriten markiert.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="profile">
        <Card>
          <CardHeader>
            <CardTitle>Mein Profil</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Name</label>
                <input type="text" className="border rounded-md p-2" defaultValue="Max Mustermann" />
              </div>
              
              <div className="grid gap-2">
                <label className="text-sm font-medium">E-Mail</label>
                <input type="email" className="border rounded-md p-2" defaultValue="max.mustermann@example.com" />
              </div>
              
              <div className="grid gap-2">
                <label className="text-sm font-medium">Telefon</label>
                <input type="tel" className="border rounded-md p-2" defaultValue="+491234567890" />
              </div>
              
              <Button className="bg-chrona-accent hover:bg-chrona-accent-light mt-4">
                Speichern
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

// Studio Owner Dashboard Component - this is a local component, renamed to avoid conflicts
const StudioOwnerDashboard = () => {
  return (
    <Tabs defaultValue="appointments">
      <TabsList className="mb-6">
        <TabsTrigger value="appointments">Terminkalender</TabsTrigger>
        <TabsTrigger value="staff">Mitarbeiter</TabsTrigger>
        <TabsTrigger value="services">Dienstleistungen</TabsTrigger>
        <TabsTrigger value="settings">Einstellungen</TabsTrigger>
      </TabsList>
      
      <TabsContent value="appointments">
        <Card>
          <CardHeader>
            <CardTitle>Terminübersicht</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-chrona-gray-400 mb-6">
              Hier siehst du alle aktuellen und bevorstehenden Termine für dein Studio.
            </p>
            
            <div className="bg-chrona-gray-800/40 border border-chrona-gray-700 rounded-lg p-8 text-center">
              <h3 className="text-xl font-medium mb-2">Studio-Dashboard wird entwickelt</h3>
              <p className="text-chrona-gray-300 mb-6">
                Der volle Funktionsumfang für Studio-Besitzer wird bald verfügbar sein.
              </p>
              <Button>Studio-Daten aktualisieren</Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="staff">
        <Card>
          <CardHeader>
            <CardTitle>Mitarbeiterverwaltung</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Mitarbeiterverwaltung wird bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="services">
        <Card>
          <CardHeader>
            <CardTitle>Dienstleistungen verwalten</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Dienstleistungsverwaltung wird bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle>Studio-Einstellungen</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Studio-Einstellungen werden bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

// Staff Dashboard Component - this is a local component, renamed to avoid conflicts
const StaffDashboard = () => {
  return (
    <Tabs defaultValue="appointments">
      <TabsList className="mb-6">
        <TabsTrigger value="appointments">Meine Termine</TabsTrigger>
        <TabsTrigger value="availability">Verfügbarkeit</TabsTrigger>
        <TabsTrigger value="settings">Einstellungen</TabsTrigger>
      </TabsList>
      
      <TabsContent value="appointments">
        <Card>
          <CardHeader>
            <CardTitle>Dein Terminplan</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-chrona-gray-400 mb-6">
              Hier siehst du alle deine aktuellen und bevorstehenden Termine.
            </p>
            
            <div className="bg-chrona-gray-800/40 border border-chrona-gray-700 rounded-lg p-8 text-center">
              <h3 className="text-xl font-medium mb-2">Mitarbeiter-Dashboard wird entwickelt</h3>
              <p className="text-chrona-gray-300 mb-6">
                Der volle Funktionsumfang für Mitarbeiter wird bald verfügbar sein.
              </p>
              <Button>Verfügbarkeit aktualisieren</Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="availability">
        <Card>
          <CardHeader>
            <CardTitle>Meine Verfügbarkeit</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Verfügbarkeitsmanagement wird bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle>Einstellungen</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Persönliche Einstellungen werden bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  return (
    <Tabs defaultValue="overview">
      <TabsList className="mb-6">
        <TabsTrigger value="overview">Übersicht</TabsTrigger>
        <TabsTrigger value="users">Nutzer</TabsTrigger>
        <TabsTrigger value="studios">Studios</TabsTrigger>
        <TabsTrigger value="settings">Systemeinstellungen</TabsTrigger>
      </TabsList>
      
      <TabsContent value="overview">
        <Card>
          <CardHeader>
            <CardTitle>System Übersicht</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-chrona-gray-400 mb-6">
              Willkommen im Admin-Dashboard. Von hier aus kannst du das gesamte System verwalten.
            </p>
            
            <div className="bg-chrona-gray-800/40 border border-chrona-gray-700 rounded-lg p-8 text-center">
              <h3 className="text-xl font-medium mb-2">Admin-Dashboard wird entwickelt</h3>
              <p className="text-chrona-gray-300 mb-6">
                Der volle Funktionsumfang für Administratoren wird bald verfügbar sein.
              </p>
              <Button>Systemstatus prüfen</Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="users">
        <Card>
          <CardHeader>
            <CardTitle>Nutzerverwaltung</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Nutzerverwaltung wird bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="studios">
        <Card>
          <CardHeader>
            <CardTitle>Studios verwalten</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Studio-Verwaltung wird bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
      
      <TabsContent value="settings">
        <Card>
          <CardHeader>
            <CardTitle>Systemeinstellungen</CardTitle>
          </CardHeader>
          <CardContent>
            <p>Systemeinstellungen werden bald verfügbar sein.</p>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};

export default Dashboard;
